import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import utils, { getUrlSearchParams, safeStringify } from 'utils';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { BUSINESS_ROUTE, URGENT_ROUTE } from 'data/global';
import { INDUSTRY_FILTER_KEY } from '../Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import { LOCATION_FILTER_KEY } from '../Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import { ObjectValues } from 'types';

export const PORTFOLIO_FILTER = {
  ALL: 'all',
  MY_DEALS: 'my_deals',
  MY_TEAM: 'my_team',
  BROKER_MANAGED: 'broker_managed',
} as const;

export type PortfolioFilter = ObjectValues<typeof PORTFOLIO_FILTER>;

export type ListingQueryFilterParams = {
  location?: string;
  industry?: string;
  from?: string;
  to?: string;
  ebitdaFrom?: string;
  ebitdaTo?: string;
  page?: string;
  portfolioFilter?: PortfolioFilter;
};

export default function useBusinessesFilters(isUrgent = false) {
  const firstURLFiltersRedirect = useRef(true);
  const initialURLFiltersValue = useRef('');

  const { params, setQueryParam } = useQueryParams<ListingQueryFilterParams>();

  const { urlFilter } = useParams<{ urlFilter: string }>();

  const { industrySelectOptions, locationOptions } =
    useOperationStepValues(true);

  const navigate = useNavigate();

  const numOfActiveFilter = useMemo(() => {
    const { page, ...restParams } = params;

    return Object.values(restParams).length;
  }, [params]);

  useEffect(() => {
    if (!urlFilter || firstURLFiltersRedirect.current) return;

    const { industry, location } = params;

    const industryArr = utils.mapParamsToArray(industry);
    const locationArr = utils.mapParamsToArray(location);

    if (
      (industryArr?.length &&
        industryArr.every((el) => el === initialURLFiltersValue.current)) ||
      (locationArr?.length &&
        locationArr.every((el) => el === initialURLFiltersValue.current))
    )
      return;

    navigate({
      search: getUrlSearchParams().toString(),
      pathname: isUrgent ? URGENT_ROUTE : BUSINESS_ROUTE,
    });
  }, [urlFilter, params, isUrgent, navigate]);

  useEffect(() => {
    if (!urlFilter || !industrySelectOptions || !locationOptions) return;

    const industryValue = industrySelectOptions.find(
      ({ value }) => utils.createSlug(value) === urlFilter,
    );

    const locationValue = locationOptions.find(
      ({ value }) => utils.createSlug(value) === urlFilter,
    );

    if (!industryValue && !locationValue) {
      navigate(isUrgent ? URGENT_ROUTE : BUSINESS_ROUTE);
      return;
    }

    if (industryValue) {
      setQueryParam(
        INDUSTRY_FILTER_KEY,
        industryValue ? safeStringify([industryValue.value]) || '' : '',
      );
      initialURLFiltersValue.current = industryValue.value;
    } else {
      setQueryParam(
        LOCATION_FILTER_KEY,
        locationValue ? safeStringify([locationValue.value]) || '' : '',
      );
      initialURLFiltersValue.current = locationValue.value;
    }

    firstURLFiltersRedirect.current = false;
  }, [
    isUrgent,
    industrySelectOptions,
    urlFilter,
    locationOptions,
    navigate,
    setQueryParam,
  ]);

  return { numOfActiveFilter };
}
